import dayjs from 'dayjs';
import {
  BreakType,
  type CreateDaySequenceInput,
} from '@core/generated/gql-types';
import type { MissingTimesheet } from '@core/timesheetApproval/missingTimesheet.types';
import { minutesToMilliseconds } from '@core/util/datetime.util';
import { formatTimeRangeAndDuration, getTimestampFromTime } from '@/utils/utils';

export interface MissingTimesheetFilters {
  search: string;
  clientNames: string[];
  accountManagers: string[];
  siteNames: string[];
}

export const EMPTY_MISSING_TIMESHEET_FILTERS: MissingTimesheetFilters = {
  search: '',
  clientNames: [],
  accountManagers: [],
  siteNames: [],
};

/** One candidate's missing shifts for a single client, shown as a single row. */
export interface MissingTimesheetGroup {
  /** `${clientId}:${userId}` so the same candidate can appear once per client. */
  rowKey: string;
  clientId: string;
  clientName: string;
  userId: string;
  workerName: string;
  workerProfileImageUrl?: string | null;
  items: MissingTimesheet[];
  startDateISO: string;
  endDateISO: string;
}

const TWELVE_HOUR_CLOCK = /^(\d{1,2}):(\d{2})\s*([AP]M)$/i;
const TWENTY_FOUR_HOUR_CLOCK = /^(\d{1,2}):(\d{2})$/;

/**
 * Predictive reports store scheduled times as `hh:mm A`. The timesheet form
 * wants `HH:mm`.
 */
export const displayClockTimeTo24Hour = (
  label?: string | null,
): string | undefined => {
  if (!label) return undefined;
  const trimmed = label.trim();
  const twelveHour = TWELVE_HOUR_CLOCK.exec(trimmed);
  if (twelveHour) {
    const hour12 = Number(twelveHour[1]);
    const minute = twelveHour[2];
    const isPm = twelveHour[3].toUpperCase() === 'PM';
    let hour24 = hour12 % 12;
    if (isPm) {
      hour24 += 12;
    }
    return `${hour24.toString().padStart(2, '0')}:${minute}`;
  }
  const twentyFourHour = TWENTY_FOUR_HOUR_CLOCK.exec(trimmed);
  if (!twentyFourHour) return undefined;
  return `${twentyFourHour[1].padStart(2, '0')}:${twentyFourHour[2]}`;
};

/**
 * Clock times for a missing shift. An end time on or before the start rolls
 * onto the next day, matching an overnight shift.
 */
export const buildMissingTimesheetSubmission = (
  item: MissingTimesheet,
  startTime: string,
  endTime: string,
  breakMinutes: number,
): CreateDaySequenceInput | null => {
  if (!item.shiftDate || !startTime || !endTime) return null;

  const clockInTimestamp = getTimestampFromTime(item.shiftDate, startTime);
  let clockOutTimestamp = getTimestampFromTime(item.shiftDate, endTime);
  if (Number(clockOutTimestamp) <= Number(clockInTimestamp)) {
    const nextDay = dayjs(item.shiftDate).add(1, 'day').format('YYYY-MM-DD');
    clockOutTimestamp = getTimestampFromTime(nextDay, endTime);
  }
  if (
    !clockInTimestamp ||
    !clockOutTimestamp ||
    Number(clockOutTimestamp) <= Number(clockInTimestamp)
  ) {
    return null;
  }

  return {
    jobId: item.jobId,
    clockInTimestamp,
    clockOutTimestamp,
    breaks:
      breakMinutes > 0
        ? [
            {
              daySequenceId: '',
              duration: String(minutesToMilliseconds(breakMinutes)),
              type: BreakType.Unpaid,
            },
          ]
        : [],
  };
};

/** Times entered for a missing shift before it is submitted. `HH:mm`. */
export interface MissingTimesheetTimes {
  startTime: string;
  endTime: string;
  breakMinutes: number;
}

export const missingTimesheetKey = (item: MissingTimesheet): string =>
  `${item.jobId}-${item.shiftDate}-${item.scheduledStartTime ?? ''}`;

/** Edited times when present, otherwise the scheduled shift with no break. */
export const resolveMissingTimesheetTimes = (
  item: MissingTimesheet,
  edited?: MissingTimesheetTimes,
): MissingTimesheetTimes =>
  edited ?? {
    startTime: displayClockTimeTo24Hour(item.scheduledStartTime) ?? '',
    endTime: displayClockTimeTo24Hour(item.scheduledEndTime) ?? '',
    breakMinutes: 0,
  };

/** Start and end as `HH:mm - HH:mm (n hrs)`, or an em dash. */
export const formatMissingShiftTimes = (
  item: MissingTimesheet,
  times: MissingTimesheetTimes,
): string => {
  const { startTime, endTime } = times;
  if (!startTime && !endTime) return '—';
  const submission = buildMissingTimesheetSubmission(
    item,
    startTime,
    endTime,
    0,
  );
  if (!submission) {
    return [startTime, endTime].filter(Boolean).join(' - ');
  }
  return formatTimeRangeAndDuration(
    submission.clockInTimestamp,
    submission.clockOutTimestamp,
  );
};

const matchesSelection = (
  selected: string[],
  value: string | null | undefined,
): boolean => selected.length === 0 || (!!value && selected.includes(value));

export const filterMissingTimesheets = (
  items: MissingTimesheet[],
  filters: MissingTimesheetFilters,
  accountManagerByClientId: ReadonlyMap<string, string> = new Map(),
): MissingTimesheet[] => {
  const search = filters.search.trim().toLowerCase();
  return items.filter((item) => {
    if (
      search &&
      ![item.workerName, item.jobTitle, item.siteName, item.clientName].some(
        (value) => value?.toLowerCase().includes(search),
      )
    ) {
      return false;
    }
    return (
      matchesSelection(filters.clientNames, item.clientName) &&
      matchesSelection(
        filters.accountManagers,
        accountManagerByClientId.get(item.clientId),
      ) &&
      matchesSelection(filters.siteNames, item.siteName)
    );
  });
};

/**
 * Groups missing shifts by client, then candidate. A candidate who worked for
 * two clients appears on two rows. Shifts in each row are sorted by date.
 */
export const groupMissingTimesheetsByClient = (
  items: MissingTimesheet[],
): MissingTimesheetGroup[] => {
  const groupsByRowKey = new Map<string, MissingTimesheetGroup>();

  items.forEach((item) => {
    const rowKey = `${item.clientId}:${item.userId}`;
    const existing = groupsByRowKey.get(rowKey);
    if (existing) {
      existing.items.push(item);
      return;
    }
    groupsByRowKey.set(rowKey, {
      rowKey,
      clientId: item.clientId,
      clientName: item.clientName?.trim() || 'Unknown client',
      userId: item.userId,
      workerName: item.workerName?.trim() || 'Unknown',
      workerProfileImageUrl: item.workerProfileImageUrl,
      items: [item],
      startDateISO: '',
      endDateISO: '',
    });
  });

  return Array.from(groupsByRowKey.values())
    .map((group) => {
      const sortedItems = [...group.items].sort((left, right) =>
        left.shiftDate.localeCompare(right.shiftDate),
      );
      const datedShifts = sortedItems
        .map((item) => item.shiftDate)
        .filter(Boolean);
      return {
        ...group,
        items: sortedItems,
        startDateISO: datedShifts[0] ?? '',
        endDateISO: datedShifts[datedShifts.length - 1] ?? '',
      };
    })
    .sort((left, right) => {
      const byClient = left.clientName.localeCompare(right.clientName);
      if (byClient !== 0) return byClient;
      return left.workerName.localeCompare(right.workerName);
    });
};
